import React, { useState, useMemo} from 'react';
import { sha256 } from 'js-sha256';
import { Container, Row, Card } from "react-bootstrap";
import { addMagicLink, getMagicLinkTable } from '../services/customerApi';

import styled from 'styled-components'
import { useTable, useFilters, useGlobalFilter, usePagination } from 'react-table'
import {matchSorter} from 'match-sorter'
import DateRangePicker from '@wojtekmaj/react-daterange-picker'

const Styles = styled.div`
  table {
    border-spacing: 0;
    border: 1px solid black;
    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }
    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;
      :last-child {
        border-right: 0;
      }
    }
  }
`

function timeConverter(timestamp) {
    var d = new Date(timestamp),
        yyyy = d.getFullYear(),
        mm = ('0' + (d.getMonth() + 1)).slice(-2),
        dd = ('0' + d.getDate()).slice(-2),
        hh = d.getHours(),
        h = hh,
        min = ('0' + d.getMinutes()).slice(-2),
        ampm = 'AM',
        time;

    if (hh > 12) {
        h = hh - 12;
        ampm = 'PM';
    } else if (hh === 12) {
        h = 12;
        ampm = 'PM';
    } else if (hh === 0) {
        h = 12;
    }

    time = yyyy + '-' + mm + '-' + dd + ', ' + h + ':' + min + ' ' + ampm;
    return time;
}


function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length

  return (
    <input
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined)
      }}
      placeholder={`Search ${count} records...`}
    />
  )
}

function NumberRangeColumnFilter({
  column: { filterValue = [], preFilteredRows, setFilter, id },
}) {
  const [rangeValue, setRangeValue] = React.useState();

  return (
    <div
      style={{
        display: 'flex',
      }}
    >
	  <DateRangePicker 
		value={rangeValue}
		onChange={e => {
			setRangeValue(e)
			const mind = e ? new Date(e[0]) : undefined
			const maxd = e ? new Date(e[1]) : undefined
			setFilter((old = []) => [mind ? mind.getTime() : undefined])
			setFilter((old = []) => [old[0], maxd ? maxd.getTime() : undefined])
        }}
	  />
    </div>
  )
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

fuzzyTextFilterFn.autoRemove = val => !val

function Table({ columns, data }) {
  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    }),
    []
  )

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page, // Instead of using 'rows', we'll use page,
    // which has only the rows for the active page

    // The rest of these things are super handy, too ;)
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
    },
    useFilters, // useFilters!
    useGlobalFilter, // useGlobalFilter!
	usePagination
  )

  // We don't want to render all of the rows for this example, so cap
  // it for this use case
  // const firstPageRows = rows.slice(0, 10)

  return (
     <>
     <div className="p-4">
     <Container>
     <Row>
     <Card className="border-0 card p-4">
	<div className="allTable">
		<div className="overFall">
		  <table {...getTableProps()}>
			<thead>
			  {headerGroups.map(headerGroup => (
				<tr {...headerGroup.getHeaderGroupProps()}>
				  {headerGroup.headers.map(column => (
					<th {...column.getHeaderProps()}>
					  {column.render('Header')}
					  {/* Render the columns filter UI */}
					  <div>{column.canFilter ? column.render('Filter') : null}</div>
					</th>
				  ))}
				</tr>
			  ))}
			</thead>
			<tbody {...getTableBodyProps()}>
			  {page.map((row, i) => {
				prepareRow(row)
				return (
				  <tr {...row.getRowProps()}>
					{row.cells.map(cell => {
					  return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
					})}
				  </tr>
				)
			  })}
			</tbody>
		  </table>
		  <br />
		</div>
		<div className="pagination">
			<div className="pageNumber">
				<span>
				  Page{' '}
				  <strong>
					{pageIndex + 1} of {pageOptions.length}
				  </strong>{' '}
				</span>
				<span>
				   | Go to page:{' '}
				  <input
					type="number"
					defaultValue={pageIndex + 1}
					onChange={e => {
					  const page = e.target.value ? Number(e.target.value) - 1 : 0
					  gotoPage(page)
					}}
				  />
				</span>{' '}
			</div>
			<div className="paginationActions">
				<button className="forwardPrev" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
				  {'<<'}
				</button>{' '}
				<button className="prev" onClick={() => previousPage()} disabled={!canPreviousPage}>
				  {'<'}
				</button>{' '}
				<button className="next" onClick={() => nextPage()} disabled={!canNextPage}>
				  {'>'}
				</button>{' '}
				<button className="forwardNext" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
				  {'>>'}
				</button>{' '}
			</div>
			<div className="paginationCount">
				<select
				  value={pageSize}
				  onChange={e => {
					setPageSize(Number(e.target.value))
				  }}
				>
				  {[1, 2, 5, 10, 20, 30, 40, 50].map(pageSize => (
					<option key={pageSize} value={pageSize}>
					  Show {pageSize}
					</option>
				  ))}
				</select>
			</div>
		  </div>
      </div>
      </Card>
      </Row>
      </Container>
      </div>
     </>
  )
}

function filterGreaterThan(rows, id, filterValue) {
  return rows.filter(row => {
    const rowValue = row.values[id]
    return rowValue >= filterValue
  })
}

filterGreaterThan.autoRemove = val => typeof val !== 'number'

const MagicLink = () => {
	const prevName = localStorage.getItem('magic_name') ? localStorage.getItem('magic_name') : '';
	const prevEmail = localStorage.getItem('magic_email') ? localStorage.getItem('magic_email') : '';
	const prevClientId = localStorage.getItem('client_id') ? localStorage.getItem('client_id') : '';
	const prevSite = localStorage.getItem('magic_site') ? localStorage.getItem('magic_site') : '';
	
	const [name, setName] = useState(prevName);
	const [lastName, setLastName] = useState(prevName);
	const [email, setEmail] = useState(prevEmail);
	const [clientId, setClientId] = useState(prevClientId);
	const [site, setSite] = useState(prevSite);
	
	const [buttonText, setButtonText] = useState('Send');
	
	const setSiteCb = (e) => {
		setSite(e.target.value);
	}
	
	const setNameCb = (e) => {
		setName(e.target.value);
	}
	const setLastNameCb = (e) => {
		setLastName(e.target.value);
	}
	const setEmailCb = (e) => {
		setEmail(e.target.value);
	}
	const setClientIdCb = (e) => {
		setClientId(e.target.value);
	}
	
	
	const saveButton = async () => {
		const message = site + "?email=" + email + "&name=" + name;
		const hash = sha256(message);
		const clearUrl = site.slice(-1);
		let allLink = '';
		if(clearUrl === '/'){
			allLink = site + 'avalanchemagic/' + hash;
		} else {
			allLink = site + '/avalanchemagic/' + hash;
		}
		console.log('hash', allLink);
		try {
			const result = await addMagicLink(name, lastName, email, clientId, hash, allLink);
			if(result && result.status){
				setButtonText('Sended');
				setTimeout(function(){
					setButtonText('Send');
				}, 2000);
				const updateTableData = await getData();
				console.log('New table data', updateTableData);
			} else {
				setButtonText('Send');
			}
		} catch(e) {
			console.log('erroe', e);
			setButtonText('Send');
		}
	}
	
	const columns = useMemo(
    () => [
        {
            Header: 'Link',
            accessor: 'link',
            filter: 'fuzzyText',
        },
        {
            Header: 'Email',
            accessor: 'email',
            filter: 'fuzzyText',
        },
        {
            Header: 'Name',
            accessor: 'name',
            filter: 'fuzzyText',
        },
        {
            Header: 'Last name',
            accessor: 'lastname',
            filter: 'fuzzyText',
        },
        {
            Header: 'Hash',
            accessor: 'hash',
            filter: 'fuzzyText',
        },
        {
            Header: 'Time Added',
            accessor: 'date',
            Filter: NumberRangeColumnFilter,
            filter: 'between',
			Cell : (props)=>{
				//props.value will contain your date
				//you can convert your date here
				if(props.value && props.value !== null){
					return timeConverter(props.value)
				} else {
					return 'Unknown'
				}
			}
        },
    ],
    []
  )
  
  const [tableData, setTableData] = React.useState([]);
  
  async function getData (){
	   const clientId = localStorage.getItem('client_id')
	   const result = await getMagicLinkTable(clientId)
	   if(result && result.msg && result.status === false){
		setTableData([])
	   } else {
		setTableData(result)
	   }
	   return result;
  }
  
  useMemo( async() => {
	  try {
		const result = await getData()
		console.log(result[0])
	  } catch (e) {
		  console.log('error', e);
	  }
  }, [])
	
    return (
		<div className="magicLinkBlock">
			<div className="customize_page p-4">
				<Container>
					   <Row>
							<Card className="col-md-12 p-4 border-0">
								<div className="settingForm">
								
									<div className="form-field">
									  <div className="field-title">
										Web site:
									  </div>
									  <div className="colorPicker">
										  <input type="text" name="site" defaultValue={site} onChange={setSiteCb} placeholder="https://mysite.domen/" />
									  </div>
									</div>
									<div className="form-field">
									  <div className="field-title">
										Name:
									  </div>
									  <div className="colorPicker">
										  <input type="text" name="name" defaultValue={name} onChange={setNameCb} placeholder="Name" />
									  </div>
									</div>
									<div className="form-field">
									  <div className="field-title">
										Last name:
									  </div>
									  <div className="colorPicker">
										  <input type="text" name="lastname" defaultValue={lastName} onChange={setLastNameCb} placeholder="Last name" />
									  </div>
									</div>
									<div className="form-field">
									  <div className="field-title">
										Email:
									  </div>
									  <div className="colorPicker">
										  <input type="text" name="email" defaultValue={email} onChange={setEmailCb} placeholder="anytext@email.domen" />
									  </div>
									</div>
									<div className="form-field">
									  <div className="field-title">
										Client ID:
									  </div>
									  <div className="colorPicker">
										  <input type="text" name="clientId" defaultValue={clientId} onChange={setClientIdCb} placeholder="Get ir form dashboard" />
									  </div>
									</div>
									<button className="ant-btn ant-btn-primary settingsButton" onClick={saveButton}>
										{buttonText}
									</button>
								</div>
							</Card>
						</Row>
				</Container>
				<Styles>
				  <Table columns={columns} data={tableData} />
				</Styles>
			</div>
		</div>
	)
}

export default MagicLink;